import { computed, ref, watch, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useConfigurationStore, useDeviceStore } from '@/stores'
import { defaultReportError } from '@/composables/useErrorLogger'
import {
  RT_BUTTONS,
  type RtFieldKey,
  type RtPresetConfig,
  type RtPresetSideValue,
  type RtSelectionMode,
} from '../rt/types'
import {
  type IMouseCalibrationFeature,
  type MouseRTCapability,
  type RtSide,
  type RtVibrationFeedbackType,
  type TriggerStatus,
  readMouseRtConfigSnapshot,
  writeMouseRtConfigSnapshot,
} from '@rapoo/device-sdk'

/** 实时行程状态轮询间隔（ms）：100ms ≈ 10Hz，肉眼可感知且对 HID 流量友好 */
const TRIGGER_STATUS_POLL_INTERVAL_MS = 100

/** 任意字段在左右键上的值映射；初始未拉取时为 undefined，按需懒加载 */
type SideMap<T> = Partial<Record<RtSide, T>>

export function useMouseRt() {
  const { t } = useI18n()
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  const configStore = useConfigurationStore()
  const { currentConfig } = storeToRefs(configStore)

  const configIndex = computed(() => currentConfig.value?.index ?? 0)

  const rtFeature = computed<IMouseCalibrationFeature | undefined>(
    () => currentDevice.value?.features?.mouseCalibration,
  )
  const rtConfig = computed(
    () =>
      currentDevice.value?.manifest.capabilities.find(
        (capability): capability is MouseRTCapability => capability.type === 'mouse-rt',
      )?.config,
  )

  // 8 个字段各自一份 left/right 值，全部走独立 getter
  const rtSwitch = ref<SideMap<boolean>>()
  const triggerPress = ref<SideMap<number>>()
  const rtLevel = ref<SideMap<number>>()
  const vibrationPress = ref<SideMap<number>>()
  const vibrationRelease = ref<SideMap<number>>()
  const vibrationFeedbackType = ref<RtVibrationFeedbackType>()
  const beepPress = ref<SideMap<number>>()
  const beepRelease = ref<SideMap<number>>()
  const beepVolume = ref<SideMap<number>>()

  const loading = ref(false)
  let initRequestId = 0

  /** 当前值只认设备回读；重新加载前清空，避免短暂展示默认值或上一档位的数据。 */
  function clearCurrentValues() {
    rtSwitch.value = undefined
    triggerPress.value = undefined
    rtLevel.value = undefined
    vibrationPress.value = undefined
    vibrationRelease.value = undefined
    vibrationFeedbackType.value = undefined
    beepPress.value = undefined
    beepRelease.value = undefined
    beepVolume.value = undefined
  }

  // ==================== 实时行程状态 (readTriggerStatus 0xB5) ====================

  /**
   * 最近一次从设备读到的实时行程状态。仅「按键触发行程」tab 启用轮询，
   * 切走或离开页面时停止；其他 tab 不展示也不读取，避免无用 HID 流量。
   */
  const triggerStatus = ref<TriggerStatus | undefined>()
  let triggerStatusTimer: ReturnType<typeof setInterval> | undefined

  /** 单次读：失败仅上报日志，不抛出；轮询继续下一次。 */
  async function pollTriggerStatus() {
    const feature = rtFeature.value
    if (!feature) {
      triggerStatus.value = undefined
      return
    }
    try {
      triggerStatus.value = await feature.readTriggerStatus()
    } catch (err) {
      defaultReportError(`Failed to read trigger status: ${JSON.stringify(err)}`)
    }
  }

  /** 启动轮询：立即读一次拿到首帧，然后按固定间隔持续读取。重复调用是幂等的。 */
  function startTriggerStatusPolling() {
    if (triggerStatusTimer !== undefined) return
    void pollTriggerStatus()
    triggerStatusTimer = setInterval(() => {
      void pollTriggerStatus()
    }, TRIGGER_STATUS_POLL_INTERVAL_MS)
  }

  /** 停止轮询并清空实时数据，避免显示陈旧状态。 */
  function stopTriggerStatusPolling() {
    if (triggerStatusTimer !== undefined) {
      clearInterval(triggerStatusTimer)
      triggerStatusTimer = undefined
    }
    triggerStatus.value = undefined
  }

  /** 按字段 key 拿到对应 ref，便于统一管理（switch 单独处理） */
  const fieldRefs: Record<Exclude<RtFieldKey, never>, Ref<SideMap<number> | undefined>> = {
    triggerPress,
    rtLevel,
    vibrationPress,
    vibrationRelease,
    beepPress,
    beepRelease,
    beepVolume,
  }

  /** 7 个左右独立数值字段、左右 RT 开关，以及 1 个共享振动反馈类型。 */
  async function init() {
    const requestId = ++initRequestId
    const device = currentDevice.value
    const feature = rtFeature.value
    const idx = configIndex.value
    clearCurrentValues()
    if (!feature) {
      loading.value = false
      return
    }

    const isCurrentRequest = () =>
      requestId === initRequestId &&
      device === currentDevice.value &&
      feature === rtFeature.value &&
      idx === configIndex.value

    loading.value = true
    try {
      const [leftSwitch, rightSwitch, feedbackType] = await Promise.all([
        feature.getRtSwitch('left', idx),
        feature.getRtSwitch('right', idx),
        feature.getVibrationFeedbackType(idx),
      ])
      if (!isCurrentRequest()) return

      const [ltPress, ltRt, ltVibP, ltVibR, ltBeepP, ltBeepR, ltBeepV] = await Promise.all([
        feature.getTriggerPress('left', idx),
        feature.getRtLevel('left', idx),
        feature.getVibrationPress('left', idx),
        feature.getVibrationRelease('left', idx),
        feature.getBeepPress('left', idx),
        feature.getBeepRelease('left', idx),
        feature.getBeepVolume('left', idx),
      ])
      if (!isCurrentRequest()) return

      const [rtPress, rtRt, rtVibP, rtVibR, rtBeepP, rtBeepR, rtBeepV] = await Promise.all([
        feature.getTriggerPress('right', idx),
        feature.getRtLevel('right', idx),
        feature.getVibrationPress('right', idx),
        feature.getVibrationRelease('right', idx),
        feature.getBeepPress('right', idx),
        feature.getBeepRelease('right', idx),
        feature.getBeepVolume('right', idx),
      ])
      if (!isCurrentRequest()) return

      rtSwitch.value = { left: leftSwitch, right: rightSwitch }
      triggerPress.value = { left: ltPress, right: rtPress }
      rtLevel.value = { left: ltRt, right: rtRt }
      vibrationPress.value = { left: ltVibP, right: rtVibP }
      vibrationRelease.value = { left: ltVibR, right: rtVibR }
      vibrationFeedbackType.value = feedbackType
      beepPress.value = { left: ltBeepP, right: rtBeepP }
      beepRelease.value = { left: ltBeepR, right: rtBeepR }
      beepVolume.value = { left: ltBeepV, right: rtBeepV }
    } catch (err) {
      if (isCurrentRequest()) {
        defaultReportError(`Failed to get mouse RT cfg: ${JSON.stringify(err)}`)
      }
    } finally {
      if (requestId === initRequestId) {
        loading.value = false
      }
    }
  }

  /** 通用：拿到一个字段当前某侧的值；尚未拉取时回退 0（slider 会渲染为最小值，不会闪到 undefined） */
  function getValue(field: RtFieldKey, side: RtSide): number {
    return fieldRefs[field].value?.[side] ?? 0
  }

  /**
   * 按字段写值并同步本地状态（按 mode 展开为 left/right/both）。
   * 振动和音效在产品层是单一设置，但 SDK 仍保留按下/释放接口，因此这里各发送两次请求。
   */
  async function setField(field: RtFieldKey, mode: RtSelectionMode, value: number) {
    const feature = rtFeature.value
    if (!feature) return
    const sides = mode === 'both' ? RT_BUTTONS : [mode]
    const idx = configIndex.value
    const target = fieldRefs[field].value ?? {}
    const updates = Object.fromEntries(sides.map((side) => [side, value]))
    const pairedField: RtFieldKey | undefined =
      field === 'vibrationPress'
        ? 'vibrationRelease'
        : field === 'vibrationRelease'
          ? 'vibrationPress'
          : field === 'beepPress'
            ? 'beepRelease'
            : field === 'beepRelease'
              ? 'beepPress'
              : undefined
    const pairedTarget = pairedField ? (fieldRefs[pairedField].value ?? {}) : undefined

    fieldRefs[field].value = { ...target, ...updates }
    if (pairedField && pairedTarget) {
      fieldRefs[pairedField].value = { ...pairedTarget, ...updates }
    }
    try {
      await Promise.all(
        sides.map((side) => {
          switch (field) {
            case 'triggerPress':
              return feature.setTriggerPress(side, value, idx)
            case 'rtLevel':
              return feature.setRtLevel(side, value, idx)
            case 'vibrationPress':
            case 'vibrationRelease':
              return Promise.all([
                feature.setVibrationPress(side, value, idx),
                feature.setVibrationRelease(side, value, idx),
              ])
            case 'beepPress':
            case 'beepRelease':
              return Promise.all([
                feature.setBeepPress(side, value, idx),
                feature.setBeepRelease(side, value, idx),
              ])
            case 'beepVolume':
              return feature.setBeepVolume(side, value, idx)
          }
        }),
      )
    } catch (err) {
      fieldRefs[field].value = target
      if (pairedField && pairedTarget) fieldRefs[pairedField].value = pairedTarget
      defaultReportError(`Failed to set mouse RT ${field}: ${JSON.stringify(err)}`)
    }
  }

  /** RT 开关是 boolean 字段，独立走这个 setter 以保留类型 */
  async function setSwitch(mode: RtSelectionMode, value: boolean) {
    const feature = rtFeature.value
    if (!feature) return
    const sides = mode === 'both' ? RT_BUTTONS : [mode]
    const idx = configIndex.value
    const target = rtSwitch.value ?? {}
    rtSwitch.value = { ...target, ...Object.fromEntries(sides.map((s) => [s, value])) }
    try {
      await Promise.all(sides.map((side) => feature.setRtSwitch(side, value, idx)))
    } catch (err) {
      rtSwitch.value = target
      defaultReportError(`Failed to set mouse RT switch: ${JSON.stringify(err)}`)
    }
  }

  /** 从当前配置档位重新读取振动反馈类型。 */
  async function getVibrationFeedbackType(): Promise<RtVibrationFeedbackType> {
    const feature = rtFeature.value
    const fallback = rtConfig.value?.vibrationFeedbackType.default as
      | RtVibrationFeedbackType
      | undefined
    if (!feature) return vibrationFeedbackType.value ?? fallback ?? 0

    try {
      const value = await feature.getVibrationFeedbackType(configIndex.value)
      vibrationFeedbackType.value = value
      return value
    } catch (err) {
      defaultReportError(`Failed to get vibration feedback type: ${JSON.stringify(err)}`)
      return vibrationFeedbackType.value ?? fallback ?? 0
    }
  }

  /** 振动反馈类型是当前配置档位下的左右键共享字段。 */
  async function setVibrationFeedbackType(value: RtVibrationFeedbackType) {
    const feature = rtFeature.value
    if (!feature || vibrationFeedbackType.value === value) return

    const previous = vibrationFeedbackType.value
    vibrationFeedbackType.value = value
    try {
      await feature.setVibrationFeedbackType(value, configIndex.value)
    } catch (err) {
      vibrationFeedbackType.value = previous
      defaultReportError(`Failed to set vibration feedback type: ${JSON.stringify(err)}`)
    }
  }

  const copySideValue = <T>(value: RtPresetSideValue<T>): SideMap<T> => ({
    left: value.left,
    right: value.right,
  })

  /** 从设备实时读取完整 RT 配置，供保存或覆盖个人预设。 */
  async function getCurrentPreset(): Promise<RtPresetConfig> {
    const feature = rtFeature.value
    if (!feature) throw new Error(t('rt.errors.configNotSupported'))
    return {
      index: configIndex.value,
      source: 'user',
      settings: await readMouseRtConfigSnapshot(feature, configIndex.value),
    }
  }

  /**
   * 将一套官方预设完整写入当前配置。
   * 先写振动类型与各数值字段，全部成功后再写 RT 开关，避免在参数尚未完成时提前启用 RT。
   * 任一写入失败都会重新读取设备状态，使页面反映设备上实际保留下来的值。
   */
  async function applyPreset(preset: RtPresetConfig): Promise<void> {
    const feature = rtFeature.value
    if (!feature) throw new Error(t('rt.errors.configNotSupported'))

    const idx = configIndex.value
    const { travel, rapidTrigger, vibration, sound } = preset.settings
    loading.value = true

    try {
      await writeMouseRtConfigSnapshot(feature, preset.settings, idx)

      triggerPress.value = copySideValue(travel.triggerPress)
      rtSwitch.value = copySideValue(rapidTrigger.rtSwitch)
      rtLevel.value = copySideValue(rapidTrigger.rtLevel)
      vibrationPress.value = copySideValue(vibration.vibrationPress)
      vibrationRelease.value = copySideValue(vibration.vibrationRelease)
      vibrationFeedbackType.value = vibration.feedbackType
      beepPress.value = copySideValue(sound.beepPress)
      beepRelease.value = copySideValue(sound.beepRelease)
      beepVolume.value = copySideValue(sound.beepVolume)
    } catch (err) {
      defaultReportError(`Failed to apply mouse RT preset: ${JSON.stringify(err)}`)
      loading.value = false
      await init()
      throw err
    } finally {
      loading.value = false
    }
  }

  watch(currentDevice, (device) => {
    // 设备断开：停止实时轮询，避免继续在已释放 feature 上读 0xB5。
    if (!device) {
      stopTriggerStatusPolling()
    }
    init()
  })

  // 设备上报 CfgChanged 后，configuration store 会更新 currentConfig；
  // RT 所有字段均按配置索引存储，因此切换后统一回读新配置的数据。
  watch(configIndex, (index, previousIndex) => {
    if (index === previousIndex) return
    init()
  })

  return {
    // 状态
    rtSwitch,
    triggerPress,
    rtLevel,
    vibrationPress,
    vibrationRelease,
    vibrationFeedbackType,
    beepPress,
    beepRelease,
    beepVolume,
    /** 当前产品 manifest 中声明的 RT 数值范围与默认值。 */
    rtConfig,
    loading,
    /** 最近一次读到的实时行程状态（仅 travel tab 启用轮询时更新） */
    triggerStatus,
    // 操作
    init,
    getValue,
    setField,
    setSwitch,
    getVibrationFeedbackType,
    setVibrationFeedbackType,
    applyPreset,
    getCurrentPreset,
    /** 启用 0xB5 周期性读取；重复调用是幂等的 */
    startTriggerStatusPolling,
    /** 停止 0xB5 周期性读取并清空已读到的实时数据 */
    stopTriggerStatusPolling,
  }
}
